import type { IRecording } from '@carboncopyco/types';
import fixWebmDuration from 'fix-webm-duration';
import { useEffect, useRef } from 'react';

import { TabType } from '@carboncopyco/types/desktop';
import { AppEventsEnum } from '../../shared/enums/AppEventsEnum';
import { useTabs } from '../store/context';
import useElectronListener from './useElectronListener';
import { useFlagsFallback } from './useFlagsFallback';

type MediaRecorders = {
  [tabId: string]: {
    screenRecorder: CloneScreenRecorder;
    cloneData: Pick<IRecording, 'id' | 'recorderUuid'>;
  };
};

type ControlHistoryAction =
  | 'startRecording'
  | 'pauseRecording'
  | 'resumeRecording'
  | 'stopRecording';

interface ControlHistoryRecord {
  action: ControlHistoryAction;
  timestamp: number;
}

class ControlHistoryLogger {
  private controlHistory: ControlHistoryRecord[];

  constructor(private tabId: string) {
    this.controlHistory = [];
  }

  public clearRecords() {
    this.controlHistory = [];
  }

  public logRecord(action: ControlHistoryAction) {
    if (action === 'startRecording') {
      this.clearRecords();
    }

    this.controlHistory.push({
      action,
      timestamp: Date.now(),
    });

    console.log(`${action} called for tab ${this.tabId}`);
  }

  public getRecordingDuration() {
    let totalDuration = 0;
    let lastActionTime: number | null = null;

    this.controlHistory.forEach((control) => {
      const isStart = control.action === 'startRecording';
      const isPause = control.action === 'pauseRecording';
      const isResume = control.action === 'resumeRecording';
      const isStop = control.action === 'stopRecording';

      if (isStart || isResume) {
        lastActionTime = control.timestamp;
      } else if (isPause && lastActionTime !== null) {
        totalDuration += control.timestamp - lastActionTime;
        lastActionTime = null;
      } else if (isStop && lastActionTime !== null) {
        totalDuration += control.timestamp - lastActionTime;
        lastActionTime = null;
      }
    });

    return totalDuration;
  }
}

class CloneScreenRecorder {
  private mediaRecorder: MediaRecorder;
  private readonly chunks: Blob[];
  private controlHistoryLogger: ControlHistoryLogger;

  constructor(private tabId: string) {
    this.chunks = [];
    this.controlHistoryLogger = new ControlHistoryLogger(tabId);
  }

  public async init(
    sourceId: string,
    onFinish: (buffer: ArrayBuffer) => Promise<void>
  ) {
    const constraints = this.getMediaConstraints(sourceId);
    const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
    this.mediaRecorder = new MediaRecorder(mediaStream, {
      mimeType: 'video/webm; codecs=vp9',
    });

    this.mediaRecorder.ondataavailable = (e: BlobEvent) =>
      this.chunks.push(e.data);

    this.mediaRecorder.onstop = async () => {
      this.controlHistoryLogger.logRecord('stopRecording');
      const videoDurationMs = this.controlHistoryLogger.getRecordingDuration();
      const blobWithoutDuration = new Blob(this.chunks, {
        type: 'video/webm; codecs=vp9',
      });

      fixWebmDuration(
        blobWithoutDuration,
        videoDurationMs,
        async (fixedBlob) => {
          await onFinish(await fixedBlob.arrayBuffer());
        },
        { logger: false }
      );
    };

    this.mediaRecorder.onstart = () => {
      this.controlHistoryLogger.logRecord('startRecording');
    };

    this.mediaRecorder.onpause = () => {
      this.controlHistoryLogger.logRecord('pauseRecording');
    };

    this.mediaRecorder.onresume = () => {
      this.controlHistoryLogger.logRecord('resumeRecording');
    };

    console.log(`Init new media recorder for tab ${this.tabId}`);
  }

  public get state() {
    return this.mediaRecorder.state;
  }

  public start() {
    this.mediaRecorder.start();
  }

  public pause() {
    this.mediaRecorder.pause();
  }

  public resume() {
    this.mediaRecorder.resume();
  }

  public stop() {
    this.mediaRecorder.stop();
  }

  private getMediaConstraints = (sourceId: string): any => ({
    audio: false,
    video: {
      mandatory: {
        chromeMediaSource: 'desktop',
        chromeMediaSourceId: sourceId,
        maxFrameRate: 15,
        minWidth: 1280,
        maxWidth: 1280,
        minHeight: 720,
        maxHeight: 720,
      },
    },
  });
}

export const useCloneScreenRecording = () => {
  const { cloneScreenRecording } = useFlagsFallback();
  const mediaRecordersRef = useRef<MediaRecorders>({});

  const { activeGlobalTab, activeRecordingData } = useTabs();

  const createMediaRecorder = async ({
    tabId,
    cloneData,
  }: {
    tabId: number;
    cloneData: IRecording;
  }) => {
    // Skip if recorder already exists
    if (mediaRecordersRef.current[`${tabId}`]) {
      return;
    }

    // Source of a main window
    const sourceId = await window.dsDesktop.app.getMainWindowSourceId();
    if (!sourceId) {
      return;
    }

    // Init new media recorder
    const screenRecorder = new CloneScreenRecorder(`${tabId}`);
    await screenRecorder.init(sourceId, async (recordingData: ArrayBuffer) => {
      try {
        await window.dsDesktop.app.saveCloneScreenRecording(
          mediaRecordersRef.current[`${tabId}`].cloneData,
          recordingData
        );
        delete mediaRecordersRef.current[`${tabId}`];
      } catch (e) {
        console.error(`Could not initialize media recorder due to: ${e}`);
      }
    });

    // Add new recorder to the map
    mediaRecordersRef.current[`${tabId}`] = {
      screenRecorder,
      cloneData: {
        id: cloneData.id,
        recorderUuid: cloneData.recorderUuid,
      },
    };
  };

  const resumeActiveTabRecording = () => {
    const cloneRecorder = mediaRecordersRef.current[`${activeGlobalTab.id}`];
    if (cloneRecorder) {
      // Resume active tab recorder
      if (cloneRecorder.screenRecorder.state === 'paused') {
        cloneRecorder.screenRecorder.resume();
      }
    }
  };

  const pauseActiveTabRecording = () => {
    const cloneRecorder = mediaRecordersRef.current[`${activeGlobalTab.id}`];
    if (cloneRecorder) {
      // Pause active tab recorder
      if (cloneRecorder.screenRecorder.state === 'recording') {
        cloneRecorder.screenRecorder.pause();
      }
    }
  };

  useElectronListener(
    AppEventsEnum.MAIN_TO_RENDERER_ON_APP_FOCUS_CHANGE,
    (isFocused: boolean) => {
      // Start / stop on app blur / focus
      if (isFocused) {
        resumeActiveTabRecording();
      } else {
        pauseActiveTabRecording();
      }
    }
  );

  /**
   * Pause / Resume screen recording based on active tab.
   * */
  useEffect(() => {
    if (!cloneScreenRecording) {
      return;
    }

    if (activeGlobalTab && activeRecordingData) {
      // Pause tabs that are not active
      Object.entries(mediaRecordersRef.current)
        .filter(([tabId]) => tabId !== `${activeGlobalTab.id}`)
        .forEach(([, recorder]) => {
          if (recorder.screenRecorder.state === 'recording') {
            recorder.screenRecorder.pause();
          }
        });

      // Resume active tab
      resumeActiveTabRecording();
    }
  }, [activeGlobalTab, activeRecordingData]);

  /**
   * Start screen recording for a specific tab.
   * @param tabId - Tab id to start recording for.
   * @param clone - Clone data to save recording for.
   */
  const startScreenRecording = async (tabId: number, cloneData: IRecording) => {
    if (!cloneScreenRecording) {
      return;
    }

    if ([TabType.CLONE, TabType.UPDATE_CLONE].includes(activeGlobalTab.type)) {
      // Start new recording
      await createMediaRecorder({
        tabId: activeGlobalTab.id,
        cloneData,
      });
    }

    mediaRecordersRef.current[`${tabId}`]?.screenRecorder?.start();
  };

  /**
   * Stop screen recording for a specific tab.
   * @param tabId - Tab id to stop recording for.
   */
  const stopScreenRecording = async (tabId: number, cloneData: IRecording) => {
    if (!cloneScreenRecording || !mediaRecordersRef.current[`${tabId}`]) {
      return;
    }

    // Save new clone data after recording is stopped
    mediaRecordersRef.current[`${tabId}`].cloneData = {
      id: cloneData.id,
      recorderUuid: cloneData.recorderUuid,
    };

    mediaRecordersRef.current[`${tabId}`].screenRecorder?.stop();
  };

  return {
    startScreenRecording,
    stopScreenRecording,
  };
};
